import clsx from 'clsx';
import * as CTC from 'common/constants/cap-table';
import * as FOC from 'common/constants/fund-ownership';
import * as SUMMARY from 'common/constants/summary';
import {
  fullyDilutedOwnershipFormat,
  largeCurrencyFormat,
  largeDecimalFormat,
  sharesFormat,
} from 'common/formats/formats';
import { DataEditorType } from 'common/types/scalarSpreadsheet';
import { FundEntityList, SecurityList } from '../components';
import EntityList from '../components/entity-list/EntityList';
import { EntityListProps } from '../components/entity-list/EntityList.props';
import EntityValueViewer from '../components/entity-list/EntityValueViewer';
import FundEntityListValueViewer from '../components/fund-entity-list/FundEntityListValueViewer';
import SecurityListValueViewer from '../components/security-list/SecurityListValueViewer';
import { FundOwnershipConfigurationRow } from '../types';

export const FundOwnershipTableAlias = {
  TITLE: 'title',
  ENTITY_REFERENCE: 'entity_reference',
  SECURITY: 'security',
  IDENTIFIER: 'identifier',
  FUND_ENTITY: FOC.FUND_ENTITY_ALIAS,
  INVESTMENT_DATE: 'investment_date',
  INVESTED_CAPITAL: 'invested_capital',
  LOAN_VALUE: 'loan_value',
  SHARES: 'shares',
  SHARES_CONVERTED: 'shares_converted',
  DILUTED_OWN_PERCENT: 'diluted_own_percent',
  INITIAL_LIQUIDATION_PREF: 'initial_liquidation_pref',
  LIQUIDATION_PREF_DIVIDENDS: 'liquidation_pref_dividends',
  CASH_DISTRIBUTION: 'cash_distribution',
  PROCEEDS_FROM_SOLD_SHARES: FOC.PROCEEDS_FROM_SOLD_SHARES_ALIAS,
  SHARES_LEDGER: FOC.SHARES_LEDGER_ALIAS,
  CASH_DISTRIBUTION_LEDGER: FOC.CASH_DISTRIBUTION_LEDGER_ALIAS,
  CUSTOM_SECURITY_NAME: FOC.CUSTOM_SECURITY_NAME_ALIAS,
};

const rowConfig = (
  hasDividends?: boolean,
  isDisabled?: boolean
): Omit<FundOwnershipConfigurationRow, 'conditions' | 'getSymbols' | 'getExprString'>[] => [
  // 01. Title
  {
    className: clsx('division-bottom-only title-col', { 'text-blue': !isDisabled }),
    hidden: false,
    isTitleOrHeader: true,
    alias: FundOwnershipTableAlias.SECURITY,
    readOnly: isDisabled,
    dataEditor: (props: any) => <SecurityList {...props} />,
    valueViewer: (props: any) => <SecurityListValueViewer {...props} />,
    isRequired: true,
  },
  // 02. Security Identifier
  {
    value: 'Security ID',
    defaultValue: null,
    alias: FundOwnershipTableAlias.IDENTIFIER,
    readOnly: isDisabled,
  },
  // 03.  Entity Reference
  {
    value: 'Entity',
    alias: FundOwnershipTableAlias.ENTITY_REFERENCE,
    hidden: false,
    readOnly: isDisabled,
    dataEditor: ((props: EntityListProps) => <EntityList {...props} />) as DataEditorType,
    valueViewer: (props: any) => <EntityValueViewer {...props} />,
    ignoreExpr: true,
    disableWhenLedgerOpen: true,
  },
  // 04.  Fund
  {
    value: 'Fund (or Entity)',
    alias: FundOwnershipTableAlias.FUND_ENTITY,
    hidden: false,
    readOnly: isDisabled,
    dataEditor: (props: any) => <FundEntityList {...props} />,
    valueViewer: (props: any) => <FundEntityListValueViewer {...props} />,
    disableWhenLedgerOpen: true,
  },
  // 05.  Investment Date
  {
    value: SUMMARY.INVESTMENT_DATE_TITLE,
    alias: FundOwnershipTableAlias.INVESTMENT_DATE,
    hidden: false,
    readOnly: true,
    dataSourceKey: 'shares_distribution_ledger_{{columnLegend}}',
    defaultValue: '',
    isRequired: true,
    dbType: 'dbDate',
    gridType: 'gridDate',
    disableWhenLedgerOpen: true,
    linkedCellSymbols: [`@${FOC.SHARES_LEDGER_ROW_NUMBER}`],
  },
  // 06.  Invested Capital
  {
    value: SUMMARY.INVESTMENT_AMOUNT_TITLE,
    alias: FundOwnershipTableAlias.INVESTED_CAPITAL,
    hidden: false,
    readOnly: true,
    format: largeCurrencyFormat,
    forceComponent: true,
    dataSourceKey: 'shares_distribution_ledger_{{columnLegend}}',
    disableEvents: true,
    hasTotal: true,
    defaultValue: 0,
    dbType: 'string',
    disableWhenLedgerOpen: true,
    linkedCellSymbols: [`@${FOC.SHARES_LEDGER_ROW_NUMBER}`],
    validateWhenReadOnly: true,
  },
  // 07.  Loan Value
  {
    value: SUMMARY.LOAN_VALUE_TITLE,
    alias: FundOwnershipTableAlias.LOAN_VALUE,
    hidden: false,
    readOnly: true,
    format: largeCurrencyFormat,
    forceComponent: true,
    dataSourceKey: 'shares_distribution_ledger_{{columnLegend}}',
    disableEvents: true,
    hasTotal: true,
    defaultValue: 0,
    dbType: 'string',
    disableWhenLedgerOpen: true,
    gridType: 'number',
    linkedCellSymbols: [`@${FOC.SHARES_LEDGER_ROW_NUMBER}`],
  },
  // 08.  Shares
  {
    value: SUMMARY.SHARES_OWNED_TITLE,
    alias: FundOwnershipTableAlias.SHARES,
    hidden: false,
    readOnly: true,
    format: sharesFormat,
    forceComponent: true,
    dataSourceKey: 'shares_distribution_ledger_{{columnLegend}}',
    disableEvents: true,
    hasTotal: true,
    defaultValue: 0,
    dbType: 'string',
    disableWhenLedgerOpen: true,
    linkedCellSymbols: [`@${FOC.SHARES_LEDGER_ROW_NUMBER}`],
    validateWhenReadOnly: true,
  },
  // 09.  Shares (as Converted)
  {
    value: 'Shares (as Converted)',
    alias: FundOwnershipTableAlias.SHARES_CONVERTED,
    hidden: false,
    readOnly: true,
    format: largeDecimalFormat,
    expr: `={{columnLegend}}${FOC.SHARES_ROW_NUMBER} * CAPTABLE("${CTC.CONVERSION_RATE_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER}) + ({{columnLegend}}${FOC.SHARES_ROW_NUMBER} / CAPTABLE("${CTC.SHARES_OUTSTANDING_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER}) * CAPTABLE("${CTC.DIVIDEND_PAYMENT_TYPE_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER}))`,
    hasTotal: true,
    gridType: 'number',
    dbType: 'string',
    defaultValue: 0,
  },
  // 10.  Fully Diluted Ownership
  {
    value: SUMMARY.FULLY_DILUTED_PERCENT_TITLE,
    alias: FundOwnershipTableAlias.DILUTED_OWN_PERCENT,
    hidden: false,
    readOnly: true,
    format: fullyDilutedOwnershipFormat,
    expr: `=IF(CAPTABLETOTAL("${CTC.TOTAL_SHARES_FULLY_DILUTED_TITLE}") != 0, {{columnLegend}}${FOC.SHARES_AS_CONVERTED_ROW_NUMBER} / CAPTABLETOTAL("${CTC.TOTAL_SHARES_FULLY_DILUTED_TITLE}"), 0)`,
    hasTotal: true,
    gridType: 'percentage',
    dbType: 'string',
    defaultValue: 0,
  },
  // 11.  Initial Liquidation Preference
  {
    value: 'Initial Liquidation Preference',
    alias: FundOwnershipTableAlias.INITIAL_LIQUIDATION_PREF,
    hidden: false,
    readOnly: true,
    format: largeCurrencyFormat,
    expr: `={{columnLegend}}${FOC.SHARES_ROW_NUMBER} * CAPTABLE("${CTC.ISSUE_PRICE_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER}) * CAPTABLE("${CTC.LIQUIDATION_PREFERENCE_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER})`,
    hasTotal: true,
    gridType: 'number',
    dbType: 'string',
    defaultValue: 0,
  },
  // 12.  Liquidation Preference Plus Accrued Dividends
  {
    value: hasDividends ? 'Liquidation Preference plus Accrued Dividends' : 'Liquidation Preference',
    alias: FundOwnershipTableAlias.LIQUIDATION_PREF_DIVIDENDS,
    hidden: false,
    readOnly: true,
    format: largeCurrencyFormat,
    expr: `={{totalSharesOutstanding}}*CAPTABLE("${CTC.ISSUE_PRICE_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER})*CAPTABLE("${CTC.LIQUIDATION_PREFERENCE_TITLE}", {{columnLegend}}${FOC.SECURITY_ROW_NUMBER})+{{multipleOfFactor}}*{{cumulativeDividends}}`,
    hasTotal: true,
    gridType: 'number',
    dbType: 'string',
    defaultValue: 0,
  },
  // 13.  Cash Distributions
  {
    value: 'Cash Distributions',
    alias: FundOwnershipTableAlias.CASH_DISTRIBUTION,
    hidden: false,
    readOnly: true,
    format: largeCurrencyFormat,
    disableEvents: true,
    hasTotal: true,
    defaultValue: 0,
    linkedCellSymbols: [`@${FOC.CASH_DISTRIBUTION_LEDGER_ROW_NUMBER}`],
  },
  // 14.  Proceeds from sold shares
  {
    value: 'Proceeds from Sold Shares',
    alias: FundOwnershipTableAlias.PROCEEDS_FROM_SOLD_SHARES,
    hidden: false,
    readOnly: true,
    format: largeCurrencyFormat,
    forceComponent: true,
    dataSourceKey: 'shares_distribution_ledger_{{columnLegend}}',
    disableEvents: true,
    hasTotal: true,
    defaultValue: 0,
    linkedCellSymbols: [`@${FOC.SHARES_LEDGER_ROW_NUMBER}`],
  },
  // 15. shares_distribution_ledger
  {
    value: 'Shares Distribution Ledger',
    alias: FundOwnershipTableAlias.SHARES_LEDGER,
    isVisible: false,
  },
  // 16. cash_distribution_ledger
  {
    value: 'Cash Distribution Ledger',
    alias: FundOwnershipTableAlias.CASH_DISTRIBUTION_LEDGER,
    isVisible: false,
  },
  // 17.  Custom Security Name
  {
    value: 'Custom Security Name',
    alias: FundOwnershipTableAlias.CUSTOM_SECURITY_NAME,
    gridType: 'string',
    dbType: 'string',
    isVisible: false,
    allowEmptyValues: true,
  },
];

export default rowConfig;
